import { useState, useEffect } from 'react';
import { KidProfile, useKidsStore } from '../../store/kidsStore';
import { useTenant } from '../../contexts/TenantContext';
import { toast } from 'sonner';
import AvatarSelector from './AvatarSelector';

interface KidProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile?: KidProfile | null;
}

export default function KidProfileModal({ isOpen, onClose, profile }: KidProfileModalProps) {
  const tenant = useTenant();
  const { createProfile, updateProfile } = useKidsStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    avatar: '👦',
    fecha_nacimiento: '',
    tiempo_diario_minutos: 30,
    politica_aceptada: false
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        nombre: profile.nombre,
        avatar: profile.avatar,
        fecha_nacimiento: profile.fecha_nacimiento,
        tiempo_diario_minutos: profile.tiempo_diario_minutos,
        politica_aceptada: true // Ya fue aceptada al crear
      });
    } else {
      setFormData({
        nombre: '',
        avatar: '👦',
        fecha_nacimiento: '',
        tiempo_diario_minutos: 30,
        politica_aceptada: false
      });
    }
  }, [profile, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.politica_aceptada && !profile) {
      toast.error('Debes aceptar la Política de Uso para Menores');
      return;
    }

    setIsSubmitting(true);

    try {
      if (profile) {
        // Actualizar
        await updateProfile(profile.id, {
          nombre: formData.nombre,
          avatar: formData.avatar,
          fecha_nacimiento: formData.fecha_nacimiento,
          tiempo_diario_minutos: formData.tiempo_diario_minutos
        });
        toast.success('Perfil actualizado exitosamente');
      } else {
        // Crear
        await createProfile(formData);
        toast.success('Perfil creado exitosamente');
      }
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Error al guardar el perfil');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-6">
          <h2
            className="text-3xl font-bold mb-2"
            style={{ color: tenant.theme.colors.primary }}
          >
            {profile ? 'Editar Perfil Kid' : 'Crear Perfil Kid'}
          </h2>
          <p className="text-gray-600">
            {profile ? 'Modifica los datos del menor' : 'Completa los datos del menor'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              Nombre del niño/a *
            </label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-primary focus:outline-none text-gray-900"
              required
            />
          </div>

          {/* Avatar */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              Avatar
            </label>
            <AvatarSelector
              selectedAvatar={formData.avatar}
              onSelect={(avatar) => setFormData({ ...formData, avatar })}
            />
          </div>

          {/* Fecha de nacimiento */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              Fecha de nacimiento *
            </label>
            <input
              type="date"
              value={formData.fecha_nacimiento}
              onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-primary focus:outline-none text-gray-900"
              required
            />
          </div>

          {/* Tiempo diario */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              Tiempo de juego diario (minutos)
            </label>
            <select
              value={formData.tiempo_diario_minutos}
              onChange={(e) => setFormData({ ...formData, tiempo_diario_minutos: parseInt(e.target.value) })}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-primary focus:outline-none text-gray-900"
            >
              <option value={15}>15 minutos</option>
              <option value={30}>30 minutos</option>
              <option value={45}>45 minutos</option>
              <option value={60}>60 minutos (1 hora)</option>
              <option value={90}>90 minutos (1.5 horas)</option>
              <option value={120}>120 minutos (2 horas)</option>
            </select>
          </div>

          {/* Política de uso (solo al crear) */}
          {!profile && (
            <div
              className="p-4 rounded-lg"
              style={{
                background: '#FFF9E6',
                borderLeft: `4px solid ${tenant.theme.colors.accent}`
              }}
            >
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.politica_aceptada}
                  onChange={(e) => setFormData({ ...formData, politica_aceptada: e.target.checked })}
                  className="mt-1 w-5 h-5"
                  required
                />
                <span className="text-sm text-gray-700 leading-relaxed">
                  He leído y acepto la{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    className="underline font-semibold"
                    style={{ color: tenant.theme.colors.primary }}
                  >
                    Política de Uso para Menores
                  </a>
                  . Declaro ser padre/madre o tutor legal del menor y tener autoridad para proporcionar este consentimiento.
                </span>
              </label>
            </div>
          )}

          {/* Botones */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-6 rounded-lg font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 py-3 px-6 rounded-lg font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: tenant.theme.colors.primary }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
